/**
 * Super Admin — BigQuery sync log
 *
 * When the metrics export last ran, and which runs failed. The export writes
 * nothing back to Supabase, so before this page the only record of a run was a
 * console.log in the sync worker — container stdout, gone on restart. BigQuery
 * keeps its own job history, and this reads it through /api/bq-jobs.
 *
 * Read-only, and deliberately has no "run export now": the worker runs the
 * export when its queue drains. Nothing here triggers a sync.
 *
 * Not a security boundary — it renders for anyone who reaches the route, but
 * /api/bq-jobs is behind authorizeMaster, so a non-master reads nothing.
 *
 * @module pages/admin/BigQuerySync
 */

import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldAlert, RefreshCw, CheckCircle2, XCircle, Database, Info, AlertTriangle, Upload } from "lucide-react";
import { YDMPageHero } from "../../components/brand";
import {
  listBigQueryJobs,
  runBigQueryExport,
  type BigQueryJobLog,
} from "../../services/bigQueryJobService";
import { BigQueryReconcile } from "@/components/admin/BigQueryReconcile";

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const formatDuration = (ms: number | null): string => {
  if (ms === null) return "—";
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
};

/**
 * BigQuery error messages run long — a real one from this project is 180 chars
 * ("...Rows: 683; errors: 1. Please look into the errors[] collection..."). Left
 * whole it either widens the table or turns one row into a paragraph, so it is
 * clamped here and carried in full by the cell's title attribute.
 */
const ERROR_MAX = 110;
const clampError = (message: string): string =>
  message.length <= ERROR_MAX ? message : `${message.slice(0, ERROR_MAX - 1).trimEnd()}…`;

const formatBytes = (bytes: number | null): string => {
  if (bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const Env: React.FC<{ children: string }> = ({ children }) => (
  <code className="text-xs bg-neutral-100 rounded px-1 py-0.5">{children}</code>
);

/** An export older than this is overdue, whatever the sync side is doing. */
const STALE_EXPORT_MS = 24 * 60 * 60 * 1000;

/**
 * What the page cannot show by listing jobs alone: whether the warehouse is
 * *current*. A run that succeeded eleven days ago looks identical to a healthy
 * one until you compare it against when the sync last wrote the source.
 */
export const attentionItems = (log: BigQueryJobLog, nowMs = Date.now()): string[] => {
  // Switched off here on purpose: another deployment exports (both tables are
  // WRITE_TRUNCATE, so only one may). "Behind" and "stale" would then be read
  // as this deployment's fault and send someone to start a worker that must
  // stay off, so this is the only thing said.
  if (!log.exportEnabled) {
    return [
      "Export is switched off on this deployment (BQ_EXPORT_ENABLED). The history below is the project's, written by whichever deployment exports.",
    ];
  }

  const items: string[] = [];
  const newest = log.jobs[0] ?? null;

  if (!newest) {
    items.push(
      "The export has never run. It fires when the sync worker drains its queue — if no worker is running, nothing will ever reach BigQuery.",
    );
  } else if (newest.error) {
    items.push(`The most recent export failed: ${newest.error}`);
  }

  // The failure that hid 778 LINE rows for eleven days: syncs kept running, the
  // worker that exports them did not.
  if (log.lastSyncAt && (!newest || new Date(log.lastSyncAt) > new Date(newest.startedAt))) {
    items.push(
      `The last successful sync finished on ${formatWhen(log.lastSyncAt)}, after the last export${
        newest ? ` on ${formatWhen(newest.startedAt)}` : ""
      } — BigQuery is behind. Only the sync worker fires an export, so check that it is running.`,
    );
  }

  // Both halves stopping used to collapse into the healthy branch: the check
  // above compares two INTERNAL timestamps, so when nothing syncs and nothing
  // exports, neither moves and the page reports a clean bill of health for a
  // warehouse that is arbitrarily stale. An absolute test against the wall
  // clock is the only thing that catches "everything is switched off".
  if (newest && nowMs - new Date(newest.startedAt).getTime() > STALE_EXPORT_MS) {
    const days = Math.floor((nowMs - new Date(newest.startedAt).getTime()) / 86_400_000);
    items.push(
      `The last export ran ${days} day${days === 1 ? "" : "s"} ago — nothing has reached BigQuery since. If the sync worker is stopped, neither half of the pipeline will report a problem.`,
    );
  }

  // Off is a choice (it keeps the export's key narrow), not a fault: only a workspace that
  // has data and no dataset needs someone. null = the check could not run, so say what is known.
  const missing = log.unprovisioned ?? null;
  if (!log.autoProvision) {
    if (missing === null) {
      items.push(
        "BQ_AUTO_PROVISION is off, so a new workspace gets no ws_<slug> dataset until someone runs scripts/bq-provision-workspaces.ts. Its data still reaches media_core.",
      );
    } else if (missing.length > 0) {
      items.push(
        `${missing.map((w) => `${w.name} (${w.dataset})`).join(", ")} ${missing.length === 1 ? "has" : "have"} data but no ws_<slug> dataset. BQ_AUTO_PROVISION is off, so run scripts/bq-provision-workspaces.ts; the data still reaches media_core.`,
      );
    }
  }
  return items;
};

const ExportNote: React.FC<{ log: BigQueryJobLog }> = ({ log }) => {
  const items = attentionItems(log);
  return (
    <div className="bg-white rounded-lg border border-neutral-200 p-4 space-y-3 text-sm">
      <h2 className="flex items-center gap-1.5 text-base font-semibold text-ink-900">
        <Info size={16} className="text-neutral-700" />
        How this export works
      </h2>

      <p className="text-neutral-700">
        <strong className="text-ink-900">One reload, every workspace at once.</strong> Each run replaces the whole
        table with everything Supabase currently holds, so there is no per-workspace job and every workspace shares
        this one history. Clients are separated afterwards, by the <Env>ws_&lt;slug&gt;</Env> views that read it.
      </p>

      <p className="text-neutral-700">
        <strong className="text-ink-900">Nothing on this page runs it.</strong> The sync worker exports once per queue
        drain — so BigQuery refreshes exactly when the data it mirrors changed, and one sync of four ad accounts still
        produces a single reload.
      </p>

      <p className="text-neutral-700">
        Configured entirely by environment: <Env>BQ_PROJECT</Env> <Env>BQ_DATASET</Env> <Env>BQ_TABLE</Env>{" "}
        <Env>BQ_LOCATION</Env> <Env>GCP_SA_KEY_JSON</Env>, plus <Env>BQ_AUTO_PROVISION</Env> (
        {log.autoProvision ? "on" : "off"}). Granting an analyst access is deliberately manual — Data Viewer on their{" "}
        <Env>ws_&lt;slug&gt;</Env> dataset and Job User on the project.
      </p>

      <p className="text-neutral-700">
        A BigQuery sandbox drops partitions older than 60 days and the limit cannot be removed, so older rows vanish on
        arrival with no error. Attaching a billing account lifts it; the free allowance covers this workload, so the
        bill stays at zero.
      </p>

      {items.length > 0 && (
        <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3">
          <h3 className="flex items-center gap-1.5 font-medium text-yellow-900">
            <AlertTriangle size={15} />
            Needs attention
          </h3>
          <ul className="mt-1.5 list-disc pl-5 space-y-1 text-yellow-900">
            {items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const Tile: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="bg-white rounded-lg border border-neutral-200 px-4 py-3">
    <p className="text-xs text-neutral-700">{label}</p>
    <p className="text-lg font-semibold text-ink-900">{value}</p>
    {hint && <p className="text-xs text-neutral-700">{hint}</p>}
  </div>
);

const AdminBigQuerySync: React.FC = () => {
  const [log, setLog] = useState<BigQueryJobLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  /**
   * What the last manual run actually moved, kept separate from `error` so a
   * failed export does not blank the job history that explains it.
   */
  const [exportResult, setExportResult] = useState<{ ok: boolean; message: string } | null>(null);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    const r = await listBigQueryJobs();
    if (r.success && r.data) {
      setLog(r.data);
      setError(null);
    } else {
      setError(r.error ?? "Failed to load BigQuery job history");
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  const runExport = useCallback(async () => {
    setExporting(true);
    setExportResult(null);
    const result = await runBigQueryExport();
    if (result.success && result.data) {
      const { rowsRead, rowsLoaded } = result.data;
      // Both numbers, always. A load that read 5,000 and accepted 0 is a
      // failure wearing a success's clothes, and the sandbox's 60-day sweep
      // means rowsLoaded is still not what SURVIVES — the note below says so.
      setExportResult({
        ok: true,
        message: `Read ${rowsRead.toLocaleString()} rows, loaded ${rowsLoaded.toLocaleString()}.`,
      });
      // Refresh the history so the run that just happened is visible in it.
      await load(true);
    } else {
      setExportResult({ ok: false, message: result.error ?? "Export failed" });
    }
    setExporting(false);
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  const hero = (
    <YDMPageHero
      eyebrow="Super Admin"
      headline="BIGQUERY SYNC"
      subtitle="Load-job history for the metrics export."
      actions={
        <div className="flex items-center gap-2">
          {/* The emergency handle. The export otherwise fires only from the sync
              worker, so a stopped worker freezes the warehouse with no remedy
              inside the app — it sat eleven days behind and the only fix was
              shell access. Disabled for the whole run: a full reload is
              WRITE_TRUNCATE, and three of them queued over each other is not a
              thing to allow by double-click. */}
          <button
            type="button"
            onClick={() => void runExport()}
            disabled={exporting || loading || refreshing || log?.exportEnabled === false}
            title={log?.exportEnabled === false ? "Export is switched off on this deployment (BQ_EXPORT_ENABLED)" : undefined}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-neutral-300 text-ink-900 hover:bg-neutral-50 disabled:opacity-50"
          >
            <Upload size={13} className={exporting ? "animate-pulse" : undefined} />
            {exporting ? "Exporting…" : "Run export now"}
          </button>
          <button
            type="button"
            onClick={() => void load(true)}
            disabled={loading || refreshing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-ink-950 text-white hover:bg-ink-900 disabled:opacity-50"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : undefined} />
            Refresh
          </button>
        </div>
      }
    />
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-ink-900 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 animate-fade-in">
        {hero}
        <div className="bg-white rounded-lg border border-neutral-200 p-8 text-center">
          <ShieldAlert className="w-12 h-12 text-yellow-700 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-ink-900 mb-2">This area is for the platform master</h2>
          <p className="text-neutral-700">{error}</p>
        </div>
      </div>
    );
  }

  // No BQ_PROJECT / service-account key on this deployment. Say so plainly —
  // an empty table would read as "the export has never run".
  if (log && !log.configured) {
    return (
      <div className="space-y-4 animate-fade-in">
        {hero}
        <div className="bg-white rounded-lg border border-neutral-200 p-8 text-center">
          <Database className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-ink-900 mb-2">BigQuery export is not configured here</h2>
          <p className="text-neutral-700 text-sm">
            Set <code className="text-xs">BQ_PROJECT</code> and <code className="text-xs">GCP_SA_KEY_JSON</code> on this
            deployment to read the job history. See{" "}
            <code className="text-xs">docs/operations/2026-08-18-bigquery-export.md</code>.
          </p>
        </div>
      </div>
    );
  }

  const jobs = log?.jobs ?? [];
  // BigQuery's `state` means "finished running", not "worked": the failed load on
  // 2026-08-20 also reads DONE. Success is DONE *and* no error — and a job still
  // running has no error yet, which must not be counted as one either way.
  const succeeded = jobs.filter((j) => !j.error && j.state === "DONE").length;
  const failed = jobs.filter((j) => j.error).length;
  // "across the last N runs" implied the two tiles sum to N; a job still
  // running counts in neither. Reworded, and the in-flight count is surfaced
  // rather than left to be inferred from the gap.
  const inFlight = jobs.length - succeeded - failed;
  const windowLabel =
    `of the last ${jobs.length} run${jobs.length === 1 ? "" : "s"}` +
    (inFlight > 0 ? ` (${inFlight} still running)` : "");

  return (
    <div className="space-y-6 animate-fade-in">
      {hero}

      {exportResult && (
        <div
          role="status"
          className={`rounded-lg border px-4 py-3 text-sm ${
            exportResult.ok
              ? "border-green-200 bg-green-50 text-green-900"
              : "border-red-200 bg-red-50 text-red-900"
          }`}
        >
          <p className="font-medium">
            {exportResult.ok ? "Export finished" : "Export failed"}
          </p>
          <p className="mt-0.5">{exportResult.message}</p>
          {exportResult.ok && (
            // Said every time, because "loaded 5,425" reads as "BigQuery now
            // holds 5,425" and it does not while the project is a sandbox:
            // partitions older than 60 days are deleted on arrival and cannot
            // be kept without a billing account.
            <p className="mt-1 text-xs text-green-800">
              While this GCP project has no billing account, BigQuery deletes partitions older than 60
              days as they land — so rows loaded is not rows kept. See{" "}
              <code className="text-xs">docs/operations/2026-08-18-bigquery-export.md</code>.
            </p>
          )}
        </div>
      )}

      {/* Did the export write the RIGHT data — the half the job history cannot
          answer. Deliberately above the run tiles: a green run list beside a
          warehouse that lost rows is exactly how the paging bug survived. */}
      <BigQueryReconcile />

      {log && <ExportNote log={log} />}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Tile label="Last run" value={jobs[0] ? formatWhen(jobs[0].startedAt) : "Never"} />
        <Tile label="Successful runs" value={String(succeeded)} hint={windowLabel} />
        <Tile label="Failed runs" value={String(failed)} hint={windowLabel} />
      </div>

      {log?.target && (
        <p className="text-sm text-neutral-700">
          Loads into <code className="text-xs">{log.target.project}</code>.
          <code className="text-xs">{log.target.dataset}</code>.<code className="text-xs">{log.target.table}</code> (
          {log.target.location}).
        </p>
      )}

      <div className="bg-white rounded-lg border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-neutral-700">
            <tr>
              <th className="px-4 py-3 font-medium">Started</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Duration</th>
              <th className="px-4 py-3 font-medium">Size</th>
              <th className="px-4 py-3 font-medium">Job ID</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {jobs.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-neutral-700">
                  No load jobs yet — the export has never written this table.
                </td>
              </tr>
            )}
            {jobs.map((job) => (
              <tr key={job.jobId}>
                <td className="px-4 py-3 whitespace-nowrap">{formatWhen(job.startedAt)}</td>
                <td className="px-4 py-3 max-w-md" title={job.error ?? undefined}>
                  {job.error ? (
                    <span className="inline-flex items-start gap-1.5 text-red-700">
                      <XCircle size={14} className="mt-0.5 shrink-0" />
                      <span className="break-words">{clampError(job.error)}</span>
                    </span>
                  ) : job.state === "DONE" ? (
                    <span className="inline-flex items-center gap-1.5 text-green-700">
                      <CheckCircle2 size={14} />
                      Succeeded
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-neutral-700">
                      <Loader2 size={14} className="animate-spin" />
                      {job.state === "RUNNING" ? "Running" : "Pending"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">{formatDuration(job.durationMs)}</td>
                <td className="px-4 py-3 whitespace-nowrap">{formatBytes(job.bytesProcessed)}</td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-700 break-all">{job.jobId}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Both limits are load-bearing: without them an empty page reads as data loss. */}
      <p className="text-xs text-neutral-700">
        BigQuery keeps job history for {log?.retentionDays ?? 180} days, and it belongs to the GCP project — a new
        production project starts with an empty log. Row counts are not recorded; size is the bytes the load processed.
      </p>
    </div>
  );
};

export default AdminBigQuerySync;
